// ====================================================================
// CLIENT FOLLOW-UP CRM - DATA STORE & BACKEND SERVICE LAYER
// ====================================================================

import { createClient } from '@supabase/supabase-js';
import { SUPABASE_CONFIG } from './config.js';

let supabase = null;
if (SUPABASE_CONFIG.url && SUPABASE_CONFIG.anonKey) {
  try {
    supabase = createClient(SUPABASE_CONFIG.url, SUPABASE_CONFIG.anonKey);
  } catch (err) {
    console.warn('Supabase initialization failed, falling back to local store:', err);
  }
}

// --------------------------------------------------------------------
// LOCAL MOCK INITIALIZATION
// --------------------------------------------------------------------
const MOCK_STORAGE_KEY = 'crm_local_db_v1';

function getTodayFormatted(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().split('T')[0];
}

function getInitialMockData() {
  const now = new Date().toISOString();
  const adminId = 'usr-admin-01';
  const staffId = 'usr-staff-01';

  const users = [
    { id: adminId, name: 'Alex Rivera (Admin)', email: 'admin@crm.com', role: 'admin', created_at: now },
    { id: staffId, name: 'Sarah Chen (Staff)', email: 'staff@crm.com', role: 'staff', created_at: now }
  ];

  const clients = [
    {
      id: 'cli-101',
      client_name: 'TechCorp Solutions',
      mobile_number: '+1 9876543210',
      alternate_mobile: '+1 9876543211',
      email: 'contact@techcorp.io',
      company: 'TechCorp Inc.',
      address: '100 Silicon Way, Tech Park',
      category: 'Hot',
      owner_id: adminId,
      created_at: now,
      updated_at: now
    },
    {
      id: 'cli-102',
      client_name: 'Apex Global Logistics',
      mobile_number: '+1 9123456789',
      alternate_mobile: '',
      email: 'sales@apexglobal.com',
      company: 'Apex Global',
      address: '45 Harbor Blvd',
      category: 'Warm',
      owner_id: staffId,
      created_at: now,
      updated_at: now
    },
    {
      id: 'cli-103',
      client_name: 'Greenfield Organics',
      mobile_number: '+1 9988776655',
      alternate_mobile: '',
      email: 'info@greenfield.org',
      company: 'Greenfield Co.',
      address: '77 Eco Lane',
      category: 'Cold',
      owner_id: adminId,
      created_at: now,
      updated_at: now
    },
    {
      id: 'cli-104',
      client_name: 'Nexus Financials',
      mobile_number: '+1 9554433221',
      alternate_mobile: '',
      email: 'finance@nexus.com',
      company: 'Nexus Group',
      address: '500 Wall Street',
      category: 'Closed',
      owner_id: staffId,
      created_at: now,
      updated_at: now
    }
  ];

  const interactions = [
    {
      id: 'int-201',
      client_id: 'cli-101',
      interaction_date: new Date(Date.now() - 86400000 * 2).toISOString(),
      status: 'Interested',
      next_followup_date: getTodayFormatted(0), // Today
      notes: 'Client reviewed demo presentation. Interested in enterprise quote.',
      created_by: adminId,
      created_at: new Date(Date.now() - 86400000 * 2).toISOString()
    },
    {
      id: 'int-202',
      client_id: 'cli-102',
      interaction_date: new Date(Date.now() - 86400000 * 5).toISOString(),
      status: 'Quotation Sent',
      next_followup_date: getTodayFormatted(-2), // Overdue
      notes: 'Sent formal PDF quote via email. Waiting for approval.',
      created_by: staffId,
      created_at: new Date(Date.now() - 86400000 * 5).toISOString()
    },
    {
      id: 'int-203',
      client_id: 'cli-103',
      interaction_date: new Date(Date.now() - 86400000 * 1).toISOString(),
      status: 'Called',
      next_followup_date: getTodayFormatted(3), // Upcoming
      notes: 'Initial discovery call completed. Asked to follow up next week.',
      created_by: adminId,
      created_at: new Date(Date.now() - 86400000 * 1).toISOString()
    },
    {
      id: 'int-204',
      client_id: 'cli-104',
      interaction_date: new Date(Date.now() - 86400000 * 10).toISOString(),
      status: 'Won',
      next_followup_date: null,
      notes: 'Contract signed! Account closed as Won.',
      created_by: staffId,
      created_at: new Date(Date.now() - 86400000 * 10).toISOString()
    }
  ];

  return { users, clients, interactions };
}

function loadLocalDB() {
  const saved = localStorage.getItem(MOCK_STORAGE_KEY);
  if (saved) {
    try { return JSON.parse(saved); } catch (e) {}
  }
  const initial = getInitialMockData();
  saveLocalDB(initial);
  return initial;
}

function saveLocalDB(data) {
  localStorage.setItem(MOCK_STORAGE_KEY, JSON.stringify(data));
}

// --------------------------------------------------------------------
// STORE EXPORTED API
// --------------------------------------------------------------------

export async function getUsers() {
  if (supabase) {
    const { data, error } = await supabase.from('profiles').select('*').order('created_at', { ascending: false });
    if (!error) return data;
  }
  const db = loadLocalDB();
  return db.users;
}

export async function updateUserRole(userId, newRole) {
  if (supabase) {
    const { error } = await supabase.from('profiles').update({ role: newRole }).eq('id', userId);
    if (error) throw error;
    return;
  }
  const db = loadLocalDB();
  const user = db.users.find(u => u.id === userId);
  if (user) {
    user.role = newRole;
    saveLocalDB(db);
  }
}

export async function getClients() {
  if (supabase) {
    const { data, error } = await supabase.from('clients').select('*, owner:profiles(name, email)').order('created_at', { ascending: false });
    if (!error) return data;
  }
  const db = loadLocalDB();
  return db.clients.map(c => {
    const owner = db.users.find(u => u.id === c.owner_id);
    return { ...c, owner: owner ? { name: owner.name, email: owner.email } : null };
  });
}

export async function getClientById(id) {
  if (supabase) {
    const { data, error } = await supabase.from('clients').select('*, owner:profiles(name, email)').eq('id', id).single();
    if (!error) return data;
  }
  const db = loadLocalDB();
  const c = db.clients.find(item => item.id === id);
  if (!c) return null;
  const owner = db.users.find(u => u.id === c.owner_id);
  return { ...c, owner: owner ? { name: owner.name, email: owner.email } : null };
}

export async function addClient(clientData) {
  // Mobile uniqueness check
  const existing = await getClientByMobile(clientData.mobile_number);
  if (existing) {
    throw new Error(`Mobile number ${clientData.mobile_number} is already registered with client "${existing.client_name}".`);
  }

  if (supabase) {
    const { data, error } = await supabase.from('clients').insert([clientData]).select().single();
    if (error) throw error;
    return data;
  }

  const db = loadLocalDB();
  const newClient = {
    ...clientData,
    id: 'cli-' + Date.now(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
  db.clients.unshift(newClient);
  saveLocalDB(db);
  return newClient;
}

export async function updateClient(id, updates) {
  if (updates.mobile_number) {
    const existing = await getClientByMobile(updates.mobile_number);
    if (existing && existing.id !== id) {
      throw new Error(`Mobile number ${updates.mobile_number} is already registered to another client.`);
    }
  }

  if (supabase) {
    const { data, error } = await supabase.from('clients').update(updates).eq('id', id).select().single();
    if (error) throw error;
    return data;
  }

  const db = loadLocalDB();
  const idx = db.clients.findIndex(c => c.id === id);
  if (idx !== -1) {
    db.clients[idx] = { ...db.clients[idx], ...updates, updated_at: new Date().toISOString() };
    saveLocalDB(db);
    return db.clients[idx];
  }
  throw new Error('Client not found');
}

export async function deleteClient(id) {
  if (supabase) {
    const { error } = await supabase.from('clients').delete().eq('id', id);
    if (error) throw error;
    return;
  }

  const db = loadLocalDB();
  db.clients = db.clients.filter(c => c.id !== id);
  db.interactions = db.interactions.filter(i => i.client_id !== id);
  saveLocalDB(db);
}

export async function getClientByMobile(mobile) {
  const cleanMobile = mobile.trim();
  if (supabase) {
    const { data } = await supabase.from('clients').select('*').eq('mobile_number', cleanMobile).maybeSingle();
    if (data) return data;
  }
  const db = loadLocalDB();
  return db.clients.find(c => c.mobile_number.trim() === cleanMobile || (c.mobile_number && c.mobile_number.replace(/\D/g, '') === cleanMobile.replace(/\D/g, ''))) || null;
}

export async function getInteractions(clientId = null) {
  if (supabase) {
    let query = supabase.from('interactions').select('*, client:clients(client_name, mobile_number, company), creator:profiles(name)').order('interaction_date', { ascending: false });
    if (clientId) query = query.eq('client_id', clientId);
    const { data, error } = await query;
    if (!error) return data;
  }

  const db = loadLocalDB();
  let list = db.interactions;
  if (clientId) {
    list = list.filter(i => i.client_id === clientId);
  }

  // Enrich with client and creator info
  return list.map(i => {
    const client = db.clients.find(c => c.id === i.client_id);
    const creator = db.users.find(u => u.id === i.created_by);
    return {
      ...i,
      client: client ? { client_name: client.client_name, mobile_number: client.mobile_number, company: client.company } : null,
      creator: creator ? { name: creator.name } : null
    };
  }).sort((a, b) => new Date(b.interaction_date) - new Date(a.interaction_date));
}

export async function addInteraction(interactionData) {
  if (supabase) {
    const { data, error } = await supabase.from('interactions').insert([interactionData]).select().single();
    if (error) throw error;
    return data;
  }

  const db = loadLocalDB();
  const newInt = {
    ...interactionData,
    id: 'int-' + Date.now(),
    created_at: new Date().toISOString()
  };
  db.interactions.unshift(newInt);
  saveLocalDB(db);
  return newInt;
}

export async function deleteInteraction(id) {
  if (supabase) {
    const { error } = await supabase.from('interactions').delete().eq('id', id);
    if (error) throw error;
    return;
  }

  const db = loadLocalDB();
  db.interactions = db.interactions.filter(i => i.id !== id);
  saveLocalDB(db);
}
